"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { RiArrowDownSLine, RiCheckLine, RiCloseLine, RiFullscreenLine } from "react-icons/ri";
import { usePathname } from "next/navigation";
import { ProgressBarLink } from "@/components/progress-bar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Button from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { noteSetupGuideOpenSource } from "@/lib/setup-checklist-analytics";
import { type SetupFilter, type SetupSection, countOpen, firstOpen, groupBySection, matchesFilter, nextOpenItem, nextUnfinishedSection, prerequisiteTitles, setupGuideQuery } from "@/lib/setup-checklist-sections";
import { cx } from "@/lib/tailwind-utils";
import type { SetupItemDetail } from "@/types/setup-checklist";
import { SetupChecklistComplete } from "./setup-checklist-complete";
import { SetupChecklistHeader } from "./setup-checklist-header";
import { SetupFilterBar } from "./setup-filter-bar";
import { SetupItemRow } from "./setup-item-row";
import { useSetupChecklist } from "@/api/provider/setup-checklist/setup-checklist-query";
import { SetupSidebarCommand, selectSetupSidebar, setupSidebarDefinition } from "@/features/sidebar/setup-sidebar";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
function clearRequestedFocus() {
  useRightSidebarStore.getState().open(setupSidebarDefinition, SetupSidebarCommand.CONSUME_FOCUS);
}
export function SetupSidebarPanel({
  onClose
}: {
  onClose?: () => void;
}) {
  const pathname = usePathname();
  const {
    data,
    isLoading,
    isError,
    refetch
  } = useSetupChecklist();
  const [filter, setFilter] = useState<SetupFilter | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const currentRoute = useMemo(() => {
    const parts = pathname.split("/");
    return parts.length > 2 ? `/${parts.slice(2).join("/")}` : pathname;
  }, [pathname]);
  const sections = useMemo(() => {
    if (!data) return [];
    const visible = data.items.filter(item => matchesFilter(item, filter));
    return groupBySection(visible);
  }, [data, filter]);

  // Prefer the open section when several match this page.
  const currentSectionKey = useMemo(() => {
    const matching = sections.filter(section => section.items.some(item_0 => item_0.status === "OPEN" && routePath(item_0) === currentRoute));
    return (matching.find(section_0 => section_0.key === openSection) ?? matching[0])?.key ?? null;
  }, [sections, currentRoute, openSection]);
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  // Scrolled after render, so it measures the expanded layout.
  const pendingScrollKey = useRef<string | null>(null);
  const openedAtRoute = useRef<string | null>(currentRoute);

  // Once per page, so a manual open on the same page is left alone.
  const handledRoute = useRef<string | null>(null);
  useEffect(() => {
    if (openedAtRoute.current !== null) {
      if (currentRoute === openedAtRoute.current) return;
      openedAtRoute.current = null;
    }
    if (!currentSectionKey || handledRoute.current === currentRoute) return;
    handledRoute.current = currentRoute;
    pendingScrollKey.current = currentSectionKey;
    setOpenSection(currentSectionKey);
  }, [currentRoute, currentSectionKey]);

  // currentRoute is a dep because setOpenSection bails when the section is already open.
  useEffect(() => {
    if (pendingScrollKey.current === null) return;
    if (pendingScrollKey.current !== openSection) return;
    const target = sectionRefs.current.get(pendingScrollKey.current);
    pendingScrollKey.current = null;
    target?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }, [openSection, currentRoute]);

  // An item opened from the full page.
  const requestedFocus = useRightSidebarStore(selectSetupSidebar).focus;
  useEffect(() => {
    if (!requestedFocus || !data) return;
    clearRequestedFocus();
    setFilter(null);
    hasAutoOpened.current = true;
    pendingScrollKey.current = requestedFocus.section;
    setOpenSection(requestedFocus.section);
    setExpandedItem(requestedFocus.itemId);
  }, [requestedFocus, data, clearRequestedFocus]);

  // A ref, so a section the user just closed does not reopen.
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (hasAutoOpened.current || sections.length === 0) return;
    hasAutoOpened.current = true;
    setOpenSection(sections.find(section_1 => !section_1.isComplete)?.key ?? null);
  }, [sections]);

  // Waits for the finished row to leave the open list, so the next one opens in the same frame.
  const pendingAdvance = useRef<{
    from: string;
    to: string | null;
  } | null>(null);
  // A ref because the row's timer calls this from a stale render.
  const latestSections = useRef(sections);
  latestSections.current = sections;
  const applyPendingAdvance = () => {
    const pending = pendingAdvance.current;
    if (!pending) return;
    const row = latestSections.current.flatMap(section_2 => section_2.items).find(item_1 => item_1.id === pending.from);
    if (row?.status === "OPEN") return;
    pendingAdvance.current = null;
    setExpandedItem(pending.to);
  };
  useEffect(applyPendingAdvance, [sections]);

  // Only advance on the change to complete, not when reading a finished section.
  const workingOn = useRef<string | null>(null);
  useEffect(() => {
    if (openSection === null) return;
    const index = sections.findIndex(section_3 => section_3.key === openSection);
    if (index === -1) return;
    if (!sections[index].isComplete) {
      workingOn.current = openSection;
      return;
    }
    if (workingOn.current !== openSection) return;
    workingOn.current = null;
    const target_0 = nextUnfinishedSection(sections, openSection);
    setOpenSection(target_0?.key ?? null);
    setExpandedItem(firstOpen(target_0?.items ?? [])?.id ?? null);
  }, [sections, openSection]);
  if (isLoading) return <SetupPanelSkeleton />;
  if (isError || !data) {
    return <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-caption text-foreground-secondary">
          Could not load your setup checklist.
        </p>
        <button type="button" onClick={() => refetch()} className="text-caption font-medium text-foreground underline-offset-2 hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          Try again
        </button>
      </div>;
  }
  if (data.items.length === 0) {
    return <div className="flex h-full items-center justify-center px-6 text-center text-caption text-foreground-secondary">
        No setup items yet.
      </div>;
  }
  const fullPageQuery = setupGuideQuery({
    items: data.items,
    expandedItem,
    section: openSection,
    filter
  });
  const fullPageHref = fullPageQuery ? `/settings/setup-guide?${fullPageQuery}` : "/settings/setup-guide";

  /** Queues the next open item in the section, wrapping to the top. */
  const advanceFrom = (item_2: SetupItemDetail, section_4: SetupSection) => {
    pendingAdvance.current = {
      from: item_2.id,
      to: nextOpenItem(section_4, item_2.id)?.id ?? null
    };
    // The refetch may already have landed while the row was celebrating.
    applyPendingAdvance();
  };
  return <div className="h-full w-full min-w-0 flex-1 overflow-hidden">
      <div className="flex h-full flex-col overflow-hidden" style={{
      width: "var(--right-sidebar-width, 100%)"
    }}>
        <div className="shrink-0 space-y-2 border-b border-border px-4 py-3">
          {onClose && <div className="flex items-center justify-between gap-2">
              <h2 className="text-body-strong text-foreground">Setup guide</h2>
              <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close setup" icon={<RiCloseLine className="size-4" />} />
            </div>}
          <SetupChecklistHeader data={data} trailing={<Button asChild variant="ghost" size="icon-sm" aria-label="Open full page" tooltip="Full page">
                <ProgressBarLink href={fullPageHref} onClick={event => {
            noteSetupGuideOpenSource(event, "sidebar");
            if (event.metaKey || event.ctrlKey) return;
            useRightSidebarStore.getState().close(setupSidebarDefinition.id);
          }}>
                  <RiFullscreenLine className="size-3.5" />
                </ProgressBarLink>
              </Button>}>
            <SetupFilterBar value={filter} onChange={setFilter} requiredOpen={countOpen(data.items, d => d.importance === "MANDATORY")} criticalOpen={countOpen(data.items, d_0 => d_0.critical)} />
          </SetupChecklistHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          {data.totals.open === 0 && !data.finishedAt && <SetupChecklistComplete className="mt-3" />}

          {sections.length === 0 ? <p className="py-6 text-center text-caption text-foreground-muted">
              Nothing matches those filters.
            </p> : <Accordion type="single" collapsible value={openSection ?? ""} onValueChange={value => setOpenSection(value || null)}>
              {sections.map(section_5 => <AccordionItem key={section_5.key} value={section_5.key} ref={el => {
            if (el) sectionRefs.current.set(section_5.key, el);else sectionRefs.current.delete(section_5.key);
          }} className="border-b-0 border-t py-3 first:border-t-0 first:pt-3">
                  <div className="flex w-full items-center gap-1.5 py-1">
                    <AccordionTrigger icon={RiArrowDownSLine} iconPosition="start" iconClassName="size-3.5 group-data-[state=closed]:-rotate-90 group-data-[state=open]:rotate-0" className="min-w-0 justify-start gap-1.5 py-0 leading-normal">
                      <span className={cx("truncate text-overline font-semibold", section_5.isComplete ? "text-foreground-muted" : "text-foreground-secondary")}>
                        {section_5.name}
                      </span>
                    </AccordionTrigger>

                    {section_5.isComplete ? <RiCheckLine className="ml-auto size-3.5 shrink-0 text-foreground-muted" aria-label="Section complete" role="img" /> : <span className="ml-auto shrink-0 text-caption tabular-nums text-foreground-muted">
                        {section_5.openCount}
                      </span>}
                  </div>

                  <AccordionContent className="pb-0 text-foreground">
                    <ul>
                      {section_5.items.map(item_3 => <SetupItemRow key={item_3.id} item={item_3} afterTitles={prerequisiteTitles(item_3, section_5)} expanded={expandedItem === item_3.id} onToggleExpanded={() => setExpandedItem(prev => prev === item_3.id ? null : item_3.id)} onSettled={() => advanceFrom(item_3, section_5)} surface="sidebar" />)}
                    </ul>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>}
        </div>
      </div>
    </div>;
}
function SetupPanelSkeleton() {
  const $ = _c(4);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-4 w-2/3" />;
    t1 = <Skeleton className="h-7 w-1/2" />;
    t2 = <Skeleton className="h-1 w-full" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex h-full w-full flex-col gap-3 px-4 py-3">{t0}{t1}{t2}<div className="space-y-2 pt-3">{Array.from({
          length: 8
        }).map(_temp)}</div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp(_, i) {
  return <Skeleton key={i} className="h-6 w-full" />;
}
function routePath(item: SetupItemDetail): string | null {
  return item.definition.route?.split("?")[0].split("#")[0] ?? null;
}